import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Upload } from 'lucide-react';
import {
  readFileAsBase64, MAX_UPLOAD_BYTES, BASE_URL,
} from './siteContentShared';
import DropZone from './DropZone';
import CustomPagesHelp from './CustomPagesHelp';
import { neonQuery } from '../../services/api';
import { getActiveEnv } from '../../services/env';

// Custom storefront pages (not BC categories) that support a banner override.
// To add another: drop a new entry here AND include the
// {{> components/atf/common/page-banner}} partial in that page's Stencil
// template. (The /recipes/ page uses an inline banner shell instead of the
// partial — historical, both paths read from the same cp_page_banners table
// via /.netlify/functions/page-banner?slug={slug}.)
const CUSTOM_PAGES = [
  { slug: 'recipes',     label: 'Recipes',         url: '/recipes/' },
  { slug: 'about-us',    label: 'About Us',        url: '/about-us/' },
  { slug: 'contact-us',  label: 'Contact Us',      url: '/contact-us/' },
  { slug: 'shipping',    label: 'Shipping',        url: '/shipping/' },
  { slug: 'vip-rewards', label: 'VIP Loyalty Club', url: '/vip-rewards/' },
];

// Banner overrides for non-category storefront pages. Backed by the
// cp_page_banners Neon table — direct upload to R2 via /r2-upload sets the
// image_url on Save. Save is per-row to match the categories pattern.
export default function CustomPagesSection({ setStatus, setPreview }) {
  const [rows, setRows] = useState({});  // { slug: { title, subhead, image_url, enabled } }
  const [edits, setEdits] = useState({}); // { slug: { title?, subhead?, image_url? } }
  const [savingSlug, setSavingSlug] = useState(null);
  const [uploadingSlug, setUploadingSlug] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await neonQuery('cp_page_banner_list', { env: getActiveEnv() });
      const arr = Array.isArray(r) ? r : (r?.rows || []);
      const byslug = {};
      for (const row of arr) byslug[row.slug] = row;
      setRows(byslug);
    } catch (e) {
      // Non-fatal; section just shows empty state
      console.warn('cp_page_banner_list failed:', e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getEdit = (slug, field) => {
    const e = edits[slug];
    if (e && field in e) return e[field];
    return rows[slug]?.[field] || '';
  };
  const setEdit = (slug, field, value) => {
    setEdits(prev => ({ ...prev, [slug]: { ...(prev[slug] || {}), [field]: value } }));
  };
  const isDirty = (slug) => {
    const e = edits[slug];
    return e && Object.keys(e).length > 0;
  };
  const resetRow = (slug) => {
    setEdits(prev => { const n = { ...prev }; delete n[slug]; return n; });
  };

  const saveRow = async (slug) => {
    if (!isDirty(slug)) return;
    setSavingSlug(slug);
    try {
      const next = {
        slug,
        title: getEdit(slug, 'title') || null,
        subhead: getEdit(slug, 'subhead') || null,
        image_url: getEdit(slug, 'image_url') || null,
        enabled: true,
      };
      const r = await neonQuery('cp_page_banner_upsert', { ...next, env: getActiveEnv() });
      const arr = Array.isArray(r) ? r : (r?.rows || []);
      if (arr[0]) setRows(prev => ({ ...prev, [slug]: arr[0] }));
      resetRow(slug);
      setStatus({ type: 'ok', msg: `Saved banner for /${slug}/` });
    } catch (e) {
      setStatus({ type: 'error', msg: `Save failed: ${e.message}` });
    } finally {
      setSavingSlug(null);
    }
  };

  // Direct upload to R2 (uses the existing r2-upload Netlify fn — its keys
  // land under marketing/ which the r2-image proxy already allow-lists).
  // Returns a public URL we drop straight into the image_url field, then
  // auto-save so the storefront picks it up without a separate Save click.
  const uploadBannerImage = async (slug, file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setStatus({ type: 'error', msg: `Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.` });
      return;
    }
    setUploadingSlug(slug);
    try {
      const dataUrl = await readFileAsBase64(file);
      const base64 = String(dataUrl).split(',')[1] || '';
      const res = await fetch(`${BASE_URL}/r2-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name || 'banner.jpg',
          contentType: file.type || 'image/jpeg',
          data: base64,
        }),
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const { url } = await res.json();
      if (!url) throw new Error('Upload returned no URL');
      const next = {
        slug,
        title: getEdit(slug, 'title') || rows[slug]?.title || null,
        subhead: getEdit(slug, 'subhead') || rows[slug]?.subhead || null,
        image_url: url,
        enabled: true,
      };
      const r = await neonQuery('cp_page_banner_upsert', { ...next, env: getActiveEnv() });
      const arr = Array.isArray(r) ? r : (r?.rows || []);
      if (arr[0]) setRows(prev => ({ ...prev, [slug]: arr[0] }));
      // Clear any staged image_url edit since it's now persisted
      setEdits(prev => {
        const n = { ...prev };
        if (n[slug]) {
          const copy = { ...n[slug] };
          delete copy.image_url;
          if (Object.keys(copy).length === 0) delete n[slug];
          else n[slug] = copy;
        }
        return n;
      });
      setStatus({ type: 'ok', msg: `Uploaded banner for /${slug}/` });
    } catch (e) {
      setStatus({ type: 'error', msg: `Upload failed: ${e.message}` });
    } finally {
      setUploadingSlug(null);
    }
  };

  const openFilePicker = (slug) => {
    uploadTargetRef.current = slug;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };
  const handleFileChosen = (e) => {
    const file = e.target.files?.[0];
    const slug = uploadTargetRef.current;
    if (!file || !slug) return;
    uploadBannerImage(slug, file);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>Custom pages</div>
        <CustomPagesHelp />
      </div>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
        Banner overrides for storefront pages that aren't BC categories. Image, title, and subhead all optional — leave blank for the page's default treatment.
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChosen}
        style={{ display: 'none' }}
      />
      <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: 'var(--gray-100)', textAlign: 'left' }}>
            <tr>
              <th style={th}>Page</th>
              <th style={{ ...th, minWidth: 280 }}>Image</th>
              <th style={th}>Banner Title override</th>
              <th style={th}>Banner Subhead override</th>
              <th style={th}>Image URL</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {CUSTOM_PAGES.map(p => {
              const dirty = isDirty(p.slug);
              const titleVal = getEdit(p.slug, 'title');
              const subheadVal = getEdit(p.slug, 'subhead');
              const imageVal = getEdit(p.slug, 'image_url');
              const saving = savingSlug === p.slug;
              const uploading = uploadingSlug === p.slug;
              return (
                <tr key={p.slug} style={{ borderTop: '1px solid var(--gray-200)', background: dirty ? '#fff8e1' : '#fff' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{p.label}</div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 11 }}>{p.url}</div>
                  </td>
                  <td style={td}>
                    <DropZone
                      disabled={uploading}
                      onFile={(file) => uploadBannerImage(p.slug, file)}
                      label={uploading ? 'Uploading...' : 'Drop image to upload'}
                      style={{ display: 'inline-block' }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {imageVal ? (
                          <button
                            type="button"
                            onClick={() => setPreview({ url: imageVal, name: p.label })}
                            title="Click to preview, or drag an image here to replace"
                            style={{ width: 240, height: 60, padding: 0, border: '1px solid var(--gray-200)', borderRadius: 4, background: '#f7f8fa', cursor: 'zoom-in', overflow: 'hidden' }}
                          >
                            <img src={imageVal} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          </button>
                        ) : (
                          <div style={{ width: 240, height: 60, border: '1px dashed var(--gray-300)', borderRadius: 4, color: 'var(--gray-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                            No image — drag here or click Upload
                          </div>
                        )}
                        <button
                          type="button" onClick={() => openFilePicker(p.slug)}
                          disabled={uploading}
                          style={{ padding: '6px 10px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: uploading ? 'wait' : 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start' }}
                        >
                          <Upload size={12} /> {uploading ? 'Uploading...' : 'Upload'}
                        </button>
                      </div>
                    </DropZone>
                  </td>
                  <td style={td}>
                    <input
                      type="text" style={input} value={titleVal}
                      onChange={(e) => setEdit(p.slug, 'title', e.target.value)}
                      placeholder={p.label}
                    />
                  </td>
                  <td style={td}>
                    <input
                      type="text" style={input} value={subheadVal}
                      onChange={(e) => setEdit(p.slug, 'subhead', e.target.value)}
                      placeholder="(blank)"
                    />
                  </td>
                  <td style={td}>
                    <input
                      type="text" style={input} value={imageVal}
                      onChange={(e) => setEdit(p.slug, 'image_url', e.target.value)}
                      placeholder="https://... (or use Upload)"
                    />
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      onClick={() => saveRow(p.slug)}
                      disabled={!dirty || saving}
                      style={{ padding: '6px 10px', background: dirty ? 'var(--orange)' : 'var(--gray-300)', color: '#fff', border: 'none', borderRadius: 4, cursor: dirty ? 'pointer' : 'not-allowed', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Save size={12} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { padding: '10px 12px', fontWeight: 600, color: 'var(--gray-600)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 };
const td = { padding: '10px 12px', verticalAlign: 'top' };
const input = { padding: '6px 8px', width: '100%', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13 };
